import partition from 'lodash/partition'
import { createSelector } from 'reselect'

import { GREAT_DEAL_VS_THRESHOLD } from '@/constants'
import type { ComposedListing, ListingsGroup } from '@/types/app-types'

import { I18N } from '../listing/types'

import type {
    GlobalPriceRangeObject,
    Group,
    ListingGlobal,
    ListingsResponse,
    ListingsStateType,
    ListingTicket,
    RecentlyViewedState,
    SeatViewObject,
    TicketSections,
} from './types'
import {
    assignGroupMapAttributes,
    composeListingsGroups,
    getInitialGroupMaps,
    getQuantityOptionsList,
    getQuantityRangeText,
    getRecentlyViewedLocalStorage,
    retrieveDeliveryMethodByListing,
} from './utils'

export const globalDataSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (listingsResponse: ListingsResponse | undefined): ListingGlobal[] | undefined => listingsResponse?.global,
)

export const testSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (listingsResponse: ListingsResponse | undefined) => {
        return { globalData: listingsResponse?.global, i18n: listingsResponse?.i18n }
    },
)

const sortedUnmappedListingsSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (listingsResponse: ListingsResponse | undefined) => {
        const sections = listingsResponse?.sections
        const listings = listingsResponse?.tickets.map((ticket) => ({
            ...ticket,
            mbi: sections?.find((s) => s.i === ticket.d)?.mbi ?? '',
        }))

        // When listings are being fetched return an empty array
        if (!listings) {
            return { listings: [], groups: [], productionId: undefined, i18n: undefined }
        }

        const productionId: number = parseFloat(listingsResponse?.global[0].productionId ?? '')

        const i18n = listingsResponse?.i18n
        const applyCurrencyConversion = !!i18n?.applyCurrencyConversion

        // a group doesn't have h & l values when there are no listings associated with it
        const groups =
            listingsResponse?.groups.filter((group) => {
                const high = applyCurrencyConversion ? group.localPrices?.h : parseFloat(group.h)
                const low = applyCurrencyConversion ? group.localPrices?.l : parseFloat(group.l)

                return !!high && !!low
            }) || []

        const [mappedListings, unmappedListings] = partition(
            listings,
            ({ c, d }: ListingTicket) => c !== '0' && d !== '0',
        )

        return {
            // Move unmapped listings to the bottom of the displayed listings
            listings: [...mappedListings, ...unmappedListings],
            groups,
            productionId,
            i18n,
        }
    },
)

export const composedListingsSelector = createSelector(
    sortedUnmappedListingsSelector,
    ({
        listings,
        groups,
        productionId,
        i18n,
    }: {
        listings: ListingTicket[]
        groups: Group[]
        productionId: number | undefined
        i18n: I18N | undefined
    }): (ComposedListing | ListingsGroup)[] => {
        const groupMaps = getInitialGroupMaps(groups.filter(({ g }) => g === '1'))

        let viewedListings: RecentlyViewedState = {}
        if (productionId && localStorage) {
            const productionRecentlyViewedStored = getRecentlyViewedLocalStorage()[productionId]
            if (productionRecentlyViewedStored) {
                viewedListings = productionRecentlyViewedStored.viewedListings
            }
        }

        const applyCurrencyConversion = !!i18n?.applyCurrencyConversion

        // Manipulate data to return objects with just the data a ListingRow needs
        const composedListings: ComposedListing[] = listings.map((listing: ListingTicket) => {
            const shouldUseLocalPrice = applyCurrencyConversion && !!listing?.localPrices

            const price = (shouldUseLocalPrice ? listing.localPrices?.p : parseInt(listing.p)) ?? 0
            const allInPrice = shouldUseLocalPrice
                ? listing.localPrices?.aip || 0
                : Math.ceil(parseFloat(listing.aip || '0'))
            const splitType: number = parseInt(listing.t)
            const quantity: number = parseInt(listing.q)
            const splitValues: string = listing.m
            const quantityOptionsList: number[] = getQuantityOptionsList(splitType, quantity, splitValues).sort(
                (a, b) => a - b,
            )
            const quantityRangeText: string = getQuantityRangeText(quantityOptionsList)

            const { c: groupId, i: id } = listing
            const inGroup = groupId in groupMaps

            const composedListing: ComposedListing = {
                id,
                price,
                // Show great deal badge if value score is greater or equal to GREAT_DEAL_VS_THRESHOLD
                isGreatDeal: !!listing.vs && parseInt(listing.vs) >= GREAT_DEAL_VS_THRESHOLD,
                isPreferredSeller: listing.f === '1',
                // Show instant delivery messaging if listing is ind (Instant Download), instantElectronicTransfer, or instantFlashSeats
                isInstantDelivery:
                    listing.ind === '1' ||
                    listing.instantElectronicTransfer === '1' ||
                    listing.instantFlashSeats === '1',
                isZoneSeating: listing.z === '1',
                quantityRangeText,
                quantityOptionsList,
                section: listing.s,
                sectionLong: listing.l,
                sectionId: listing.d,
                groupId,
                row: listing.r,
                notes: listing.n,
                defaultQuantity: quantityOptionsList[0],
                deliveryMethod: retrieveDeliveryMethodByListing(listing),
                faceValue: parseFloat(listing.faceValue || '0'),
                allInPrice,
                stockTypeId: listing.st,
                inGroup,
                isGroup: false,
                sectionColor: `#${groups.find(({ i }) => i === groupId)?.c?.slice(2) ?? 'fff'}`,
                isRecentlyViewed: !!viewedListings[id],
                mbi: listing?.mbi,
                shouldUseLocalPrice,
                badges: listing.badges,
            }

            if (inGroup) {
                assignGroupMapAttributes(groupMaps[groupId], composedListing)
            }

            return composedListing
        })

        const listingsGroups: ListingsGroup[] = composeListingsGroups(groupMaps)

        return [...listingsGroups, ...composedListings]
    },
)

export const seatViewObjectSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (listingsResponse: ListingsResponse | undefined): SeatViewObject | undefined => {
        if (!listingsResponse) {
            return undefined
        }

        const { global, tickets, sections } = listingsResponse
        const [commonGlobalInfo] = global
        const { mapTitle, svgFileName, jsonFileName, staticMapUrl } = commonGlobalInfo

        const ticketSections: TicketSections = {}
        tickets.forEach((ticket: ListingTicket) => {
            const { d: ticketSectionId } = ticket
            const section = sections.find(({ i: sectionId }) => sectionId === ticketSectionId)
            ticketSections[ticketSectionId] = { ticket, section }
        })

        return { mapTitle, svgFileName, jsonFileName, staticMapUrl, ticketSections }
    },
)

export const globalPriceRangeObjectSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (listingsResponse: ListingsResponse | undefined): GlobalPriceRangeObject => {
        const globalData = listingsResponse?.global

        if (!globalData?.length) {
            // Use zeros as placeholders until data is retrieved
            return {
                lowestPrice: 0,
                medianPrice: 0,
                highestPrice: 0,
                lowestAllInPrice: 0,
                medianAllInPrice: 0,
                highestAllInPrice: 0,
            }
        }

        if (listingsResponse?.i18n?.applyCurrencyConversion && globalData[0].localPrices) {
            return {
                lowestPrice: Math.ceil(globalData[0].localPrices.lp),
                medianPrice: Math.ceil(globalData[0].localPrices.mp),
                highestPrice: Math.ceil(globalData[0].localPrices.hp),
                lowestAllInPrice: Math.ceil(globalData[0].localPrices.lowestAip || 0),
                medianAllInPrice: Math.ceil(globalData[0].localPrices.medianAip || 0),
                highestAllInPrice: Math.ceil(globalData[0].localPrices.highestAip || 0),
            }
        }

        return {
            lowestPrice: parseInt(globalData[0].lp),
            medianPrice: parseInt(globalData[0].mp),
            highestPrice: parseInt(globalData[0].hp),
            lowestAllInPrice: Math.ceil(parseFloat(globalData[0].lowestAip || '0')),
            medianAllInPrice: Math.ceil(parseFloat(globalData[0].medianAip || '0')),
            highestAllInPrice: Math.ceil(parseFloat(globalData[0].highestAip || '0')),
        }
    },
)

export const mapJsonFileNameSelector = createSelector(
    globalDataSelector,
    (globalData: ListingGlobal[] | undefined): string => globalData?.[0]?.jsonFileName || '',
)

export const mapboxMapLayoutUrl = createSelector(
    globalDataSelector,
    (globalData: ListingGlobal[] | undefined): string => globalData?.[0]?.mapboxLayoutUrl || '',
)

export const isFaceValueSelector = createSelector(
    globalDataSelector,
    (globalData: ListingGlobal[] | undefined): boolean => {
        return globalData?.[0]?.isFaceValue === 'true'
    },
)

export const showAipSelector = createSelector(
    globalDataSelector,
    (globalData: ListingGlobal[] | undefined): boolean => globalData?.[0]?.showAip === 'true',
)

export const hasListingsSelector = createSelector(
    globalDataSelector,
    (globalData: ListingGlobal[] | undefined): boolean => (globalData?.[0]?.listingCount || '0') !== '0',
)

export const i18nListingsDataSelector = createSelector(
    (state: ListingsStateType) => state.data,
    (data: ListingsResponse | undefined): I18N | undefined => data?.i18n,
)

export const listingsInSelectedSectionSelector = createSelector(
    [composedListingsSelector, (state: ListingsStateType, sectionId: string | undefined) => sectionId],
    (listings, sectionId) =>
        listings.filter((composedListing) => composedListing.sectionId === sectionId && !composedListing.isGroup),
)
